import { useCallback, useEffect, useMemo, useState } from 'react';
import employeesApi from 'Api/axios/employees-api';
import type { AssignableCourseI } from 'types';

/**
 * Catálogo de cursos que el empleado todavía no tiene.
 *
 * El filtrado y la paginación son en memoria: el endpoint devuelve el catálogo
 * completo de un empleado, que son decenas de cursos. `SelectCoursesDialog`
 * pide igualmente las props de paginación, así que se las damos calculadas
 * aquí en vez de añadir paginación al servidor para un caso que no la necesita.
 *
 * El backend ya entrega la forma que el diálogo consume, así que aquí no se
 * aplana nada.
 */
const useAssignableCourses = (employeeId: string) => {
  const [all, setAll] = useState<AssignableCourseI[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearchState] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const refetch = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    const response = await employeesApi.readAssignableCourses(employeeId);
    setAll(response.data ?? []);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) return all;

    return all.filter(course => course.name.toLocaleLowerCase().includes(needle));
  }, [all, search]);

  const setSearch = (value: string) => {
    setSearchState(value);
    setPage(1);
  };

  return {
    courses: filtered.slice((page - 1) * pageSize, page * pageSize),
    loading,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalItems: filtered.length,
    totalPages: Math.ceil(filtered.length / pageSize),
    refetch,
  };
};

export default useAssignableCourses;
